import { TAB_ENUM } from '../../Page/SelfCarePage/Constant';

export const PRODUCT_SERVICE_WORLD = {
  Internet: 0,
  TV: 1,
  Cellular: 2,
  Abroad: 3,
  Energy: 4,
} as const;

export const FAULT_TYPE = {
  zone: 'zone',
  powerSupply: 'powerSupply',
  none: 'none',
} as const;

export type FaultType = (typeof FAULT_TYPE)[keyof typeof FAULT_TYPE];

export type DetectFaultResult = {
  type: FaultType;
  data: unknown;
};


export function normalizeProductWorldFromCms(productWorldProp) {
  if (productWorldProp == null) {
    return undefined;
  }
  if (typeof productWorldProp === 'object' && productWorldProp !== null && 'value' in productWorldProp) {
    return normalizeProductWorldFromCms((productWorldProp as { value: unknown }).value);
  }
  if (typeof productWorldProp === 'number' && Number.isFinite(productWorldProp)) {
    if (productWorldProp < 0) {
      return undefined;
    }
    return productWorldProp;
  }
  if (typeof productWorldProp === 'string') {
    const t = productWorldProp.trim();
    if (t === '') {
      return undefined;
    }
    if (/^\d+$/.test(t)) {
      return Number(t);
    }
  }
  return undefined;
}

type CmsDomainRowLike = {
  productWorld?: unknown;
  contentLink?: { id?: number | string };
};
export function resolveDomainRowFromCms<T extends CmsDomainRowLike>(
  domainItems: T[] | undefined | null,
  productWorld: number,
  domainContentLinkId?: number | string | null,
): T | undefined {
  const list = domainItems ?? [];
  if (domainContentLinkId != null && domainContentLinkId !== '') {
    const byId = list.find(
      (item) =>
        item.contentLink?.id != null && String(item.contentLink.id) === String(domainContentLinkId),
    );
    if (byId != null && normalizeProductWorldFromCms(byId.productWorld) === productWorld) {
      return byId;
    }
  }
  return list.find((item) => normalizeProductWorldFromCms(item.productWorld) === productWorld);
}

export function tabKeyFromProductWorld(world: number): string | null {
  switch (world) {
    case PRODUCT_SERVICE_WORLD.Internet:
      return TAB_ENUM.Internet;
    case PRODUCT_SERVICE_WORLD.TV:
      return TAB_ENUM.TV;
    case PRODUCT_SERVICE_WORLD.Cellular:
      return TAB_ENUM.Cellular;
    case PRODUCT_SERVICE_WORLD.Abroad:
      return TAB_ENUM.Abroad;
    case PRODUCT_SERVICE_WORLD.Energy:
      return TAB_ENUM.Energy;
    default:
      return null;
  }
}

export function getChosenProductNameForReport(productWorld: number | null | undefined): string {
  switch (productWorld) {
    case PRODUCT_SERVICE_WORLD.Internet:
      return 'Fiber';
    case PRODUCT_SERVICE_WORLD.TV:
      return 'TV';
    case PRODUCT_SERVICE_WORLD.Cellular:
      return 'Cellular';
    case PRODUCT_SERVICE_WORLD.Abroad:
      return 'Abroad';
    default:
      return '';
  }
}

export function getMalfunctionTypeForReport(faultType: FaultType | null | undefined): string {
  switch (faultType) {
    case FAULT_TYPE.powerSupply:
      return 'שנאי';
    case FAULT_TYPE.zone:
      return 'תקלה אזורית';
    default:
      return '';
  }
}

export function isSubscribersByProductLoaded(
  subscribersByProduct: unknown,
): subscribersByProduct is Record<string, Record<string, unknown>[]> {
  return (
    subscribersByProduct != null &&
    typeof subscribersByProduct === 'object' &&
    !Array.isArray(subscribersByProduct)
  );
}

export function getSubscriptionsForProductWorld(
  subscribersByProduct: unknown,
  productWorld: number,
): Record<string, unknown>[] {
  const tabKey = tabKeyFromProductWorld(productWorld);
  if (!tabKey || !isSubscribersByProductLoaded(subscribersByProduct)) {
    return [];
  }
  const list = subscribersByProduct[tabKey];
  return Array.isArray(list) ? list : [];
}

export function hasSubscriptionsForProductWorld(
  subscribersByProduct: unknown,
  productWorldFromCms: unknown,
): boolean {
  const productWorld = normalizeProductWorldFromCms(productWorldFromCms);
  if (productWorld == null || productWorld < 0) {
    return false;
  }
  return getSubscriptionsForProductWorld(subscribersByProduct, productWorld).length > 0;
}

export function linesCellularLike(sub: Record<string, unknown>): { primary: string; secondary?: string } {
  const phone = String(sub.productSubscriberNo ?? '').trim();
  const first = String(sub.productFirstName ?? '').trim();
  const last = String(sub.productLastName ?? '').trim();
  const name = `${first} ${last}`.trim();
  if (name) {
    return { primary: name, secondary: phone || undefined };
  }
  return { primary: phone };
}
export function linesEnergy(sub: Record<string, unknown>): { primary: string; secondary?: string } {
  const raw = sub.customerAddress as string | undefined;
  const primary = raw ? raw.trim().replace(/^,|,$/g, '') : '';
  const secondary = String(
    (sub as { socBillDescription?: string }).socBillDescription ??
      sub.productDesc ??
      sub.packageName ??
      '',
  ).trim();
  return secondary ? { primary, secondary } : { primary };
}

export function linesForWorld(world: number, sub: Record<string, unknown>): { primary: string; secondary?: string } {
  switch (world) {
    case PRODUCT_SERVICE_WORLD.Cellular:
    case PRODUCT_SERVICE_WORLD.Abroad:
      return linesCellularLike(sub);
    case PRODUCT_SERVICE_WORLD.Energy:
      return linesEnergy(sub);
    default:
      return { primary: String(sub.productSubscriberNo ?? '') };
  }
}

export function shouldCheckZoneFaultStatus(productWorld: number): boolean {
  return productWorld === PRODUCT_SERVICE_WORLD.Internet;
}

const ZONE_FAULT_NO_ISSUE_STATUSES = ['networkNormal', 'issueResolved', 'plannedJobFinished'] as const;

export function hasActiveZoneFault(zoneFault: { status?: string } | null | undefined): boolean {
  const status = zoneFault?.status;
  if (!status) {
    return false;
  }
  return !ZONE_FAULT_NO_ISSUE_STATUSES.includes(status as (typeof ZONE_FAULT_NO_ISSUE_STATUSES)[number]);
}

export function isPowerSupplyFaultDetected(powerSupplyFault: unknown): boolean {
  if (powerSupplyFault == null || typeof powerSupplyFault !== 'object') {
    return false;
  }

  const response = powerSupplyFault as {
    Body?: {
      isRecommendSuplyPower?: boolean;
    };
  };

  return response.Body?.isRecommendSuplyPower === true;
}

export function normalizePhoneNumber(phone: unknown): string {
  return String(phone ?? '').replace(/\D/g, '');
}

export function formatPhoneNumber(phone: string): string {
  if (phone.length === 10 && phone.startsWith('05')) {
    return `${phone.slice(0, 3)}-${phone.slice(3)}`;
  }

  return phone;
}

type ProblemDomainLoginDetails = {
  firstName?: string;
  contactNumber?: string;
};

/** Contact first name, else name on subscription, else login phone (contactNumber). */
export function getProblemDomainGreetingName(
  loginDetails: ProblemDomainLoginDetails | undefined,
  subscriptionRaw: Record<string, unknown> | undefined,
): string {
  const contactFirstName = String(loginDetails?.firstName ?? '').trim();
  if (contactFirstName.length > 1) {
    return contactFirstName;
  }

  const productFirstName = String(subscriptionRaw?.productFirstName ?? '').trim();
  if (productFirstName) {
    return productFirstName;
  }

  const loginPhone = normalizePhoneNumber(loginDetails?.contactNumber);
  if (loginPhone) {
    return formatPhoneNumber(loginPhone);
  }

  return '';
}
